/**
 * Sub-hook for PO Lines bulk selection and bulk operations (CR-3408).
 *
 * CR-5972: Bulk action handlers accept an optional ``ids`` argument so the
 * grouped action bar can scope each click to the subset of lines that match
 * the clicked status group. When ``ids`` is omitted, the action runs over
 * the full ``selectedLineIds`` set (preserves the original flat-bar behaviour).
 */
import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { purchasingAPI, PurchaseOrderLine } from '../../../../lib/api';
import { useAuth } from '../../../../contexts/AuthContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { useUserPermissions } from '../../../../contexts/UserPermissionsContext';
import { isFullyInSupplierWarehouse } from './utils';

interface BulkResultEntry {
  lineId: number;
  poNumber: string;
  lineNumber: number;
  error: string;
}

interface UsePOLinesBulkActionsParams {
  lines: PurchaseOrderLine[];
  fetchLines: () => Promise<void>;
  bustCacheRef: React.MutableRefObject<boolean>;
}

export function usePOLinesBulkActions({ lines, fetchLines, bustCacheRef }: UsePOLinesBulkActionsParams) {
  const { user } = useAuth();
  const { hasPurchasingWriteAccess } = useUserPermissions();
  const { isFeatureEnabled } = useMenuSettings();

  // Bulk selection state
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set());
  const [bulkAccepting, setBulkAccepting] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [bulkProposing, setBulkProposing] = useState(false);
  const [showBulkAcceptModal, setShowBulkAcceptModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [showBulkHoldModal, setShowBulkHoldModal] = useState(false);
  const [showBulkProposeModal, setShowBulkProposeModal] = useState(false);
  // CR-5972: the grouped bar fires the same modal for either "Accept All" (Changed
  // lines) or "Acknowledge All" (New lines). Stashing the verb + scoped ids when the
  // bar opens the confirmation lets the handler use the right toast label and only
  // mutate the relevant subset.
  const [pendingAcceptIds, setPendingAcceptIds] = useState<Set<number> | null>(null);
  const [pendingAcceptVerb, setPendingAcceptVerb] = useState<'accept' | 'acknowledge'>('accept');
  const [pendingRejectIds, setPendingRejectIds] = useState<Set<number> | null>(null);
  const [pendingHoldIds, setPendingHoldIds] = useState<Set<number> | null>(null);
  const [pendingProposeIds, setPendingProposeIds] = useState<Set<number> | null>(null);
  const [bulkHolding, setBulkHolding] = useState(false);

  // Bulk operation result log
  const [bulkResultLog, setBulkResultLog] = useState<BulkResultEntry[]>([]);
  const [showBulkResultLog, setShowBulkResultLog] = useState(false);

  // Only allow selecting lines where the action is waiting on the current user's role.
  // CR-4203: Also checks role-based site permissions for bulk operations
  const canSelectForBulkAccept = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4687 round 4: keep the cancelled / on_hold guards, but drop the
    // `status === 'received'` exclusion. The PO-line `status` (pending /
    // received / cancelled) is the goods-receipt lifecycle and is
    // independent of the `acknowledge_status` workflow that bulk
    // accept/reject acts on. Suppliers in test data routinely have
    // ack='new' AND status='received' (customer GRN'd before the
    // supplier acknowledged), and the previous exclusion made every
    // such line non-selectable — i.e. select-all stayed unchecked,
    // no row checkboxes rendered, bulk buttons stayed disabled.
    // The authoritative "is there work for this user" signal is
    // `waiting_on`, which the next checks already gate on.
    if (line.status === 'cancelled' || line.on_hold) return false;

    const isCustomer = user.platform === 'CUSTOMER' || user.platform === 'HELIX';
    const isSupplier = user.platform === 'SUPPLIER';

    // Supplier-side line closure (call-off suppliers only): an in-stock
    // line is no longer actionable by the supplier, so exclude it from bulk
    // select (and from Select All).
    if (isSupplier && isFeatureEnabled('switch_call_off_tab') && isFullyInSupplierWarehouse(line)) return false;

    // CR-4647: Site-based write access only applies to customer users.
    // Suppliers don't have permissions on customer sites, so the check would
    // always return false and hide bulk actions from them.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    if (isCustomer && line.waiting_on === 'Customer') return true;
    if (isSupplier && line.waiting_on === 'Supplier') return true;

    // CR-5971: WIP / accepted lines have no formal `waiting_on` value (neither
    // side owes the next move in the acknowledgement workflow), but they are
    // still actionable — both roles can Put On Hold and Propose Change against
    // them, and the grouped action bar (CR-5972) already exposes those two
    // actions in its `wip` group. Without this, suppliers can't select WIP
    // lines individually or via Select All so the buttons never render.
    if (line.acknowledge_status === 'wip' || line.acknowledge_status === 'accepted') {
      return isCustomer || isSupplier;
    }

    return false;
  }, [user, hasPurchasingWriteAccess, isFeatureEnabled]);

  const getSelectableLines = useCallback(() => {
    return lines.filter(line => canSelectForBulkAccept(line));
  }, [lines, canSelectForBulkAccept]);

  // CR-4687 round 3: Sarah reported that the selection count drifted out of
  // sync with reality after per-line actions — UI said "3 selected" but only
  // remaining lines were actually selected because a status transition (e.g.
  // New → WIP) removed the actioned line from the selectable set. Prune the
  // selection set whenever ``lines`` refetches so the count always reflects
  // currently-selectable rows, regardless of which action triggered the
  // refetch (per-line modal, bulk action, filter change, page change).
  useEffect(() => {
    setSelectedLineIds(prev => {
      if (prev.size === 0) return prev;
      const selectableIds = new Set(
        lines.filter(line => canSelectForBulkAccept(line)).map(line => line.id)
      );
      const pruned = new Set(Array.from(prev).filter(id => selectableIds.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [lines, canSelectForBulkAccept]);

  const handleSelectAll = useCallback(() => {
    const selectable = getSelectableLines();
    if (selectedLineIds.size === selectable.length && selectable.length > 0) {
      setSelectedLineIds(new Set());
    } else {
      setSelectedLineIds(new Set(selectable.map(l => l.id)));
    }
  }, [getSelectableLines, selectedLineIds.size]);

  const handleToggleSelect = useCallback((lineId: number) => {
    setSelectedLineIds(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(lineId)) {
        newSelected.delete(lineId);
      } else {
        newSelected.add(lineId);
      }
      return newSelected;
    });
  }, []);

  // CR-5972: shared accept/acknowledge worker. ``verb`` controls the toast
  // copy — same API endpoint for both, just a different label so suppliers
  // see "Acknowledged" for new lines and "Accepted" for changed lines.
  const runBulkAccept = useCallback(async (ids: Set<number>, verb: 'accept' | 'acknowledge') => {
    if (ids.size === 0) return;

    setBulkAccepting(true);
    let successCount = 0;
    const failedLines: BulkResultEntry[] = [];

    const lineMap = new Map(lines.map(l => [l.id, l]));

    for (const lineId of Array.from(ids)) {
      try {
        await purchasingAPI.acceptDeliveryDate(lineId, {});
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const errorMsg = axiosErr?.response?.data?.message || (err instanceof Error ? err.message : 'Unknown error');
        const lineInfo = lineMap.get(lineId);
        failedLines.push({
          lineId,
          poNumber: lineInfo?.po_number || String(lineId),
          lineNumber: lineInfo?.line_number || 0,
          error: errorMsg,
        });
      }
    }

    setBulkAccepting(false);

    const past = verb === 'acknowledge' ? 'Acknowledged' : 'Accepted';
    const fail = verb === 'acknowledge' ? 'acknowledge' : 'accept';
    if (successCount > 0) {
      toast.success(`${past} ${successCount} line${successCount > 1 ? 's' : ''} successfully`);
    }
    if (failedLines.length > 0) {
      setSelectedLineIds(new Set(failedLines.map(f => f.lineId)));
      setBulkResultLog(failedLines);
      setShowBulkResultLog(true);
      toast.error(`Failed to ${fail} ${failedLines.length} line${failedLines.length > 1 ? 's' : ''} — see details below`);
    } else {
      // Only clear when there were no failures to retry, and only the ids we
      // processed — leaves any unprocessed status groups still selected.
      setSelectedLineIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    }

    bustCacheRef.current = true;
    fetchLines();
  }, [lines, fetchLines, bustCacheRef]);

  const handleBulkAccept = useCallback(async (ids?: Set<number>) => {
    const target = ids ?? pendingAcceptIds ?? selectedLineIds;
    const verb = ids ? 'accept' : (pendingAcceptVerb ?? 'accept');
    await runBulkAccept(target, verb);
    setPendingAcceptIds(null);
    setPendingAcceptVerb('accept');
  }, [runBulkAccept, pendingAcceptIds, pendingAcceptVerb, selectedLineIds]);

  // CR-5972: Acknowledge All (for ``new`` lines) — same endpoint as Accept All
  // (acceptDeliveryDate) with a relabelled toast. Surfaces the same confirmation
  // modal as Accept, just with the verb set to "acknowledge".
  const requestBulkAccept = useCallback((ids: Set<number>, verb: 'accept' | 'acknowledge') => {
    setPendingAcceptIds(ids);
    setPendingAcceptVerb(verb);
    setShowBulkAcceptModal(true);
  }, []);

  const requestBulkReject = useCallback((ids: Set<number>) => {
    setPendingRejectIds(ids);
    setShowBulkRejectModal(true);
  }, []);

  const requestBulkHold = useCallback((ids: Set<number>) => {
    setPendingHoldIds(ids);
    setShowBulkHoldModal(true);
  }, []);

  const requestBulkPropose = useCallback((ids: Set<number>) => {
    setPendingProposeIds(ids);
    setShowBulkProposeModal(true);
  }, []);

  const handleBulkReject = useCallback(async (reason: string, ids?: Set<number>) => {
    const target = ids ?? pendingRejectIds ?? selectedLineIds;
    if (target.size === 0 || !reason) return;

    setBulkRejecting(true);
    let successCount = 0;
    const failedLines: BulkResultEntry[] = [];

    const lineMap = new Map(lines.map(l => [l.id, l]));

    for (const lineId of Array.from(target)) {
      try {
        await purchasingAPI.declineDeliveryDate(lineId, { reason });
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const errorMsg = axiosErr?.response?.data?.message || (err instanceof Error ? err.message : 'Unknown error');
        const lineInfo = lineMap.get(lineId);
        failedLines.push({
          lineId,
          poNumber: lineInfo?.po_number || String(lineId),
          lineNumber: lineInfo?.line_number || 0,
          error: errorMsg,
        });
      }
    }

    setBulkRejecting(false);
    setShowBulkRejectModal(false);
    setPendingRejectIds(null);

    if (successCount > 0) {
      toast.success(`Rejected ${successCount} line${successCount > 1 ? 's' : ''} successfully`);
    }
    if (failedLines.length > 0) {
      setSelectedLineIds(new Set(failedLines.map(f => f.lineId)));
      setBulkResultLog(failedLines);
      setShowBulkResultLog(true);
      toast.error(`Failed to reject ${failedLines.length} line${failedLines.length > 1 ? 's' : ''} — see details below`);
    } else {
      setSelectedLineIds(prev => {
        const next = new Set(prev);
        target.forEach(id => next.delete(id));
        return next;
      });
    }

    bustCacheRef.current = true;
    fetchLines();
  }, [pendingRejectIds, selectedLineIds, lines, fetchLines, bustCacheRef]);

  const handleBulkPutOnHold = useCallback(async (reason: string, ids?: Set<number>) => {
    const target = ids ?? pendingHoldIds ?? selectedLineIds;
    if (target.size === 0 || !reason) return;

    setBulkHolding(true);
    let successCount = 0;
    let failCount = 0;

    for (const lineId of Array.from(target)) {
      try {
        await purchasingAPI.putLineOnHold(lineId, { reason });
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to put line ${lineId} on hold:`, err);
      }
    }

    setBulkHolding(false);
    setShowBulkHoldModal(false);
    setPendingHoldIds(null);

    setSelectedLineIds(prev => {
      const next = new Set(prev);
      target.forEach(id => next.delete(id));
      return next;
    });

    if (successCount > 0) {
      toast.success(`Put ${successCount} line${successCount > 1 ? 's' : ''} on hold`);
    }
    if (failCount > 0) {
      toast.error(`Failed to put ${failCount} line${failCount > 1 ? 's' : ''} on hold`);
    }

    bustCacheRef.current = true;
    fetchLines();
  }, [pendingHoldIds, selectedLineIds, fetchLines, bustCacheRef]);

  // CR-5972: Bulk Propose Change — applies a single proposed delivery date +
  // reason to every line in the scoped set. The per-line propose-change flow
  // opens the detail modal with separate date/qty/price legs; for the bulk
  // action we keep it intentionally narrow (date only) because that's the
  // common path and a multi-axis bulk form would be too dangerous to fan
  // across many lines blindly. Suppliers who need to propose quantity or
  // price on a specific line can still do so per-line via the detail modal.
  const handleBulkProposeChange = useCallback(async (
    data: { proposedDate: string; reason: string; comments?: string },
    ids?: Set<number>,
  ) => {
    const target = ids ?? pendingProposeIds ?? selectedLineIds;
    if (target.size === 0 || !data.proposedDate || !data.reason) return;

    setBulkProposing(true);
    let successCount = 0;
    const failedLines: BulkResultEntry[] = [];

    const lineMap = new Map(lines.map(l => [l.id, l]));

    for (const lineId of Array.from(target)) {
      try {
        await purchasingAPI.proposeAlternativeDate(lineId, {
          proposedDate: data.proposedDate,
          reason: data.reason,
          comments: data.comments,
        });
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const errorMsg = axiosErr?.response?.data?.message || (err instanceof Error ? err.message : 'Unknown error');
        const lineInfo = lineMap.get(lineId);
        failedLines.push({
          lineId,
          poNumber: lineInfo?.po_number || String(lineId),
          lineNumber: lineInfo?.line_number || 0,
          error: errorMsg,
        });
      }
    }

    setBulkProposing(false);
    setShowBulkProposeModal(false);
    setPendingProposeIds(null);

    if (successCount > 0) {
      toast.success(`Proposed change on ${successCount} line${successCount > 1 ? 's' : ''} successfully`);
    }
    if (failedLines.length > 0) {
      setSelectedLineIds(new Set(failedLines.map(f => f.lineId)));
      setBulkResultLog(failedLines);
      setShowBulkResultLog(true);
      toast.error(`Failed to propose change on ${failedLines.length} line${failedLines.length > 1 ? 's' : ''} — see details below`);
    } else {
      setSelectedLineIds(prev => {
        const next = new Set(prev);
        target.forEach(id => next.delete(id));
        return next;
      });
    }

    bustCacheRef.current = true;
    fetchLines();
  }, [pendingProposeIds, selectedLineIds, lines, fetchLines, bustCacheRef]);

  return {
    selectedLineIds,
    setSelectedLineIds,
    bulkAccepting,
    bulkRejecting,
    bulkHolding,
    bulkProposing,
    showBulkAcceptModal,
    setShowBulkAcceptModal,
    showBulkRejectModal,
    setShowBulkRejectModal,
    showBulkHoldModal,
    setShowBulkHoldModal,
    showBulkProposeModal,
    setShowBulkProposeModal,
    pendingAcceptIds,
    pendingAcceptVerb,
    pendingRejectIds,
    pendingHoldIds,
    pendingProposeIds,
    bulkResultLog,
    showBulkResultLog,
    setShowBulkResultLog,
    canSelectForBulkAccept,
    getSelectableLines,
    handleSelectAll,
    handleToggleSelect,
    handleBulkAccept,
    handleBulkReject,
    handleBulkPutOnHold,
    handleBulkProposeChange,
    requestBulkAccept,
    requestBulkReject,
    requestBulkHold,
    requestBulkPropose,
  };
}
